import { useEffect, useState } from "react";
import axios from "axios";
import Cal, { getCalApi } from "@calcom/embed-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2, Send, Video } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CAL_LINK = process.env.REACT_APP_CAL_LINK;
const CAL_URL = `https://cal.com/${CAL_LINK}`;
const CONTACT_EMAIL = "enquiry@norcev.se";

const INITIAL = { name: "", email: "", company: "", company_size: "", budget: "", message: "" };

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 outline-none transition-all duration-300 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(29,78,216,0.12)]";

const InfoCard = () => {
  const { t } = useLang();
  const label = "font-mono-alt text-[11px] uppercase tracking-[0.25em] text-blue-700";
  return (
    <motion.aside
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      data-testid="contact-info-card"
      className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white shadow-sm"
    >
      <div className="p-7">
        <div className={label}>{t.contact.quickChat}</div>
        <p className="mt-3 text-base font-normal leading-relaxed text-slate-700">{t.contact.quickChatText}</p>
        <a
          data-testid="contact-schedule-button"
          href={CAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono-alt group mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-blue-700 transition-colors hover:text-blue-900"
        >
          {t.contact.schedule}
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </div>
      <div className="p-7">
        <div className={label}>{t.contact.email}</div>
        <a
          data-testid="contact-email-link"
          href={`mailto:${CONTACT_EMAIL}`}
          className="mt-3 block text-base text-slate-700 transition-colors hover:text-blue-700"
        >
          {CONTACT_EMAIL}
        </a>
      </div>
      <div className="p-7">
        <div className={label}>{t.contact.offices}</div>
        <ul className="mt-3 space-y-1.5 text-base text-slate-700">
          {t.contact.officeList.map((o, i) => (
            <li key={o} data-testid={`contact-office-${i}`}>{o}</li>
          ))}
        </ul>
      </div>
    </motion.aside>
  );
};

const InquiryForm = () => {
  const { t, lang } = useLang();
  const f = t.contact.form;
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        lang,
        company_size: form.company_size || null,
        budget: form.budget || null,
        company: form.company || null,
      });
      toast.success(t.contact.toastOk, { description: t.contact.toastOkDesc });
      setForm(INITIAL);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(t.contact.toastErr, {
        description: typeof detail === "string" ? detail : t.contact.toastErrDesc,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="p-6 sm:p-8">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <input data-testid="contact-name-input" required minLength={2} placeholder={f.name} value={form.name} onChange={set("name")} className={inputCls} />
        <input data-testid="contact-email-input" required type="email" placeholder={f.email} value={form.email} onChange={set("email")} className={inputCls} />
        <input data-testid="contact-company-input" placeholder={f.company} value={form.company} onChange={set("company")} className={inputCls} />
        <select
          data-testid="contact-size-select"
          value={form.company_size}
          onChange={set("company_size")}
          className={`${inputCls} ${form.company_size ? "" : "!text-slate-400"} appearance-none`}
        >
          <option value="" disabled>{f.size}</option>
          {["1–10", "11–50", "51–200", "200+"].map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
        <select
          data-testid="contact-budget-select"
          value={form.budget}
          onChange={set("budget")}
          className={`${inputCls} ${form.budget ? "" : "!text-slate-400"} appearance-none sm:col-span-2`}
        >
          <option value="" disabled>{f.budget}</option>
          {["< $10k", "$10k – $50k", "$50k – $150k", "$150k+"].map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
        <textarea
          data-testid="contact-message-input"
          required
          minLength={10}
          rows={4}
          placeholder={f.message}
          value={form.message}
          onChange={set("message")}
          className={`${inputCls} resize-none sm:col-span-2`}
        />
      </div>
      <button
        data-testid="contact-submit-button"
        type="submit"
        disabled={loading}
        className="group mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-blue-700 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-800 hover:shadow-[0_12px_32px_rgba(29,78,216,0.28)] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            {f.submit}
            <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </>
        )}
      </button>
      <p className="font-mono-alt mt-4 text-center text-[10px] uppercase tracking-[0.2em] text-slate-500">{f.note}</p>
    </form>
  );
};

const BookingEmbed = () => {
  const { t } = useLang();
  useEffect(() => {
    (async () => {
      const cal = await getCalApi({ namespace: "30min" });
      cal("ui", {
        theme: "light",
        cssVarsPerTheme: { light: { "cal-brand": "#1d4ed8" } },
        hideEventTypeDetails: false,
        layout: "month_view",
      });
    })();
  }, []);

  return (
    <div data-testid="contact-booking-embed" className="p-2 sm:p-4">
      <div className="font-mono-alt mb-3 flex items-center gap-2 px-2 text-[11px] uppercase tracking-[0.2em] text-slate-500">
        <Video className="h-3.5 w-3.5 text-teal-600" />
        {t.contact.calNote}
      </div>
      <div className="min-h-[520px] overflow-hidden rounded-xl">
        <Cal
          namespace="30min"
          calLink={CAL_LINK}
          style={{ width: "100%", height: "100%", overflow: "auto" }}
          config={{ layout: "month_view", theme: "light", useSlotsViewOnSmallScreen: "true", name: "", email: "" }}
        />
      </div>
    </div>
  );
};

export const Contact = () => {
  const { t } = useLang();
  const [tab, setTab] = useState("book");

  return (
    <section id="contact" data-testid="contact-section" className="noise-overlay relative py-20 lg:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 50% 60% at 20% 50%, rgba(29,78,216,0.05), transparent 70%)" }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-12 max-w-2xl"
        >
          <div className="font-mono-alt mb-4 text-xs uppercase tracking-[0.25em] text-blue-700">{t.contact.eyebrow}</div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl lg:text-5xl lg:leading-[1.1]">
            {t.contact.title} <span className="text-glow-gradient">{t.contact.titleAccent}</span>
          </h2>
          <p className="mt-5 max-w-md text-base font-normal leading-relaxed text-slate-700">{t.contact.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)] lg:col-span-8"
          >
            <div className="flex border-b border-slate-200 bg-slate-50" role="tablist" data-testid="contact-tabs">
              {[
                { id: "book", label: t.contact.tabBook },
                { id: "inquiry", label: t.contact.tabInquiry },
              ].map((tb) => (
                <button
                  key={tb.id}
                  role="tab"
                  aria-selected={tab === tb.id}
                  data-testid={`contact-tab-${tb.id}`}
                  onClick={() => setTab(tb.id)}
                  className={`relative flex-1 px-5 py-4 text-sm font-semibold transition-colors duration-300 ${
                    tab === tb.id ? "bg-white text-blue-700" : "text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {tb.label}
                  {tab === tb.id && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-blue-700" />}
                </button>
              ))}
            </div>
            {tab === "book" ? <BookingEmbed /> : <InquiryForm />}
          </motion.div>

          <div className="lg:col-span-4">
            <InfoCard />
          </div>
        </div>
      </div>
    </section>
  );
};
